// src/context/UsernameContext.jsx
import { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAccount } from 'wagmi';
import { useUsername } from '@/hooks/useUsername';
import { useAppAuth } from '@/hooks/useAppAuth';

const UsernameContext = createContext({
  username: null,
  isLoading: false,
  showDialog: false,
  setShowDialog: () => {},
  hasCheckedUsername: false,
  suggestedUsername: null,
});

export const UsernameProvider = ({ children }) => {
  const { address, isConnected } = useAccount();
  const { data: username, isLoading } = useUsername(address);
  const { user: appUser, status: authStatus } = useAppAuth();
  const isBackendAuthenticated = authStatus === 'authenticated';
  const backendUser = appUser;
  const [showDialog, setShowDialog] = useState(false);
  const [hasCheckedUsername, setHasCheckedUsername] = useState(false);
  const [suggestedUsername, setSuggestedUsername] = useState(null);

  // Check if user needs to set username on wallet connection
  useEffect(() => {
    if (isConnected && address && !isLoading && hasCheckedUsername) {
      // User has connected and we've checked for username
      if (!username) {
        // If the signed-in backend user carries a username the username
        // query doesn't return (an incompatible name), suggest it.
        if (isBackendAuthenticated && backendUser?.username) {
          // Sanitize it into the allowed username format
          const sanitized = backendUser.username.replace(/-/g, '_');
          if (/^[a-zA-Z0-9_]{3,20}$/.test(sanitized)) {
            setSuggestedUsername(sanitized);
          }
        }
        setShowDialog(true);
      }
    }
  }, [isConnected, address, username, isLoading, hasCheckedUsername, isBackendAuthenticated, backendUser]);

  // Mark as checked once we've loaded the username (or confirmed it doesn't exist)
  useEffect(() => {
    if (isConnected && address && !isLoading) {
      setHasCheckedUsername(true);
    } else if (!isConnected) {
      // Reset when disconnected
      setHasCheckedUsername(false);
      setShowDialog(false);
      setSuggestedUsername(null);
    }
  }, [isConnected, address, isLoading]);

  const value = {
    username,
    isLoading,
    showDialog,
    setShowDialog,
    hasCheckedUsername,
    suggestedUsername,
  };

  return (
    <UsernameContext.Provider value={value}>
      {children}
    </UsernameContext.Provider>
  );
};

UsernameProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useUsernameContext = () => {
  const context = useContext(UsernameContext);
  if (!context) {
    throw new Error('useUsernameContext must be used within UsernameProvider');
  }
  return context;
};
